import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Horse, Timer, CurrencyDollar, Users, ChartLineUp, MapTrifold, MagnifyingGlass, CalendarBlank, MapPin, ArrowRight, Lightning, Trophy, Devices, Envelope, Phone, ChatCircleDots } from '@phosphor-icons/react';
import api from '../utils/api';
import { toast } from 'sonner';

const LandingPage = () => {
  const navigate = useNavigate();
  const [upcomingShows, setUpcomingShows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactSending, setContactSending] = useState(false);

  useEffect(() => {
    api.get('/shows?state=LIVE').then(({ data }) => {
      setUpcomingShows(data.slice(0, 6));
    }).catch(() => {});
  }, []);

  const filteredShows = upcomingShows.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.location || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      toast.error('Please fill out all fields');
      return;
    }
    setContactSending(true);
    try {
      await api.post('/contact', contactForm);
      toast.success('Message sent! We\'ll get back to you soon.');
      setContactForm({ name: '', email: '', message: '' });
    } catch {
      toast.error('Failed to send message. Please email us directly.');
    } finally {
      setContactSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden">
      {/* Hero Background */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1760041870271-496f3d0d46ee?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
          alt="Background"
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/50 to-[#0A0A0A]"></div>
      </div>

      <div className="relative z-10">
        {/* Nav */}
        <nav className="bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_2decf1d9-6688-4b47-98c1-1893beae897a/artifacts/lr28yf8y_43425DE4-4AE4-4784-89F6-4AA746534747.png"
                  alt="Show Master Logo"
                  className="h-[60px] w-[60px] sm:h-[72px] sm:w-[72px]"
                />
                <span className="text-lg sm:text-2xl font-black text-white" style={{fontFamily: 'Outfit'}}>SHOW MASTER</span>
              </div>
              <div className="flex gap-2 sm:gap-4">
                <button data-testid="landing-browse-btn" onClick={() => navigate('/shows')}
                  className="hidden sm:block px-4 sm:px-6 py-2 bg-transparent border border-[#262626] hover:border-[#0096FF] text-white rounded-md transition-colors text-sm">
                  Browse Shows
                </button>
                <button data-testid="landing-login-btn" onClick={() => navigate('/login')}
                  className="px-4 sm:px-6 py-2 bg-transparent border border-[#262626] hover:border-[#0096FF] text-white rounded-md transition-colors text-sm">
                  Login
                </button>
                <button data-testid="landing-signup-btn" onClick={() => navigate('/register')}
                  className="px-4 sm:px-6 py-2 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors text-sm">
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="py-16 sm:py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="mb-4 sm:mb-6">
              <span className="text-[10px] sm:text-xs tracking-[0.2em] uppercase font-semibold text-[#0096FF]">THE COMPLETE PLATFORM</span>
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl tracking-tight font-black uppercase text-white mb-4 sm:mb-6 glow-text" style={{fontFamily: 'Outfit'}}>
              Run Your Horse Shows
              <br />
              <span className="text-[#00D4FF]">Like a Pro</span>
            </h1>
            <p className="text-sm sm:text-lg text-[#9E9E9E] max-w-2xl mx-auto mb-8 sm:mb-12 leading-relaxed px-4">
              From entries to payouts, manage your entire equestrian event on one powerful platform. Less paperwork, more ridin'.
            </p>

            {/* Two-audience CTA split */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 max-w-3xl mx-auto px-4" data-testid="audience-cta-split">
              {/* Producers */}
              <button
                onClick={() => navigate('/register')}
                className="group text-left bg-[#0F1A2A] border border-[#1f3558] hover:border-[#0096FF] rounded-xl p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_40px_-10px_rgba(0,150,255,0.4)]"
                data-testid="hero-cta-producer"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Trophy size={20} weight="fill" className="text-[#0096FF]" />
                  <span className="text-[10px] sm:text-xs tracking-[0.2em] uppercase font-bold text-[#0096FF]">For Producers & Organizations</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-black text-white mb-1.5" style={{ fontFamily: 'Outfit' }}>
                  Run Your Show
                </h3>
                <p className="text-xs sm:text-sm text-[#9E9E9E] mb-4 leading-relaxed">
                  Manage entries, draws, payouts, and live results — all in one place.
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-[#0096FF] group-hover:bg-[#00D4FF] px-4 py-2 rounded-md transition-colors">
                  Start a Show <ArrowRight size={14} weight="bold" />
                </span>
              </button>

              {/* Riders */}
              <button
                onClick={() => navigate('/shows')}
                className="group text-left bg-[#0F1F0F] border border-[#1f3d1f] hover:border-[#34C759] rounded-xl p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_40px_-10px_rgba(52,199,89,0.4)]"
                data-testid="hero-cta-rider"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Horse size={20} weight="fill" className="text-[#34C759]" />
                  <span className="text-[10px] sm:text-xs tracking-[0.2em] uppercase font-bold text-[#34C759]">For Riders</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-black text-white mb-1.5" style={{ fontFamily: 'Outfit' }}>
                  Find Your Next Show
                </h3>
                <p className="text-xs sm:text-sm text-[#9E9E9E] mb-4 leading-relaxed">
                  Browse upcoming shows, reserve your stall, and enter in under 2 minutes.
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-[#34C759] group-hover:bg-[#2ea94a] px-4 py-2 rounded-md transition-colors">
                  Browse Shows <ArrowRight size={14} weight="bold" />
                </span>
              </button>
            </div>

            <p className="text-[11px] sm:text-xs text-[#555] mt-5">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-[#0096FF] hover:text-[#00D4FF] font-semibold underline-offset-2 hover:underline transition-colors"
                data-testid="hero-login-link"
              >
                Log in
              </button>
            </p>
          </div>
        </section>

        {/* How It Works — Riders */}
        <section className="py-16 sm:py-20 bg-[#0A0A0A]" data-testid="how-it-works">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-[10px] sm:text-xs tracking-[0.2em] uppercase font-semibold text-[#34C759]">FOR RIDERS</span>
              <h2 className="text-2xl sm:text-3xl font-black text-white mt-2" style={{fontFamily: 'Outfit'}}>Enter a Show in 3 Steps</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { step: '1', title: 'Find a Show', desc: 'Browse upcoming shows by location, date, or discipline. See classes, fees, and open stalls at a glance.', icon: MagnifyingGlass, color: '#0096FF' },
                { step: '2', title: 'Enter Online', desc: 'Pick your classes, reserve stalls, select your exact spot on the barn map, and pay securely — all from your phone.', icon: Lightning, color: '#FF9500' },
                { step: '3', title: 'Show Up & Ride', desc: 'Your entry is confirmed, draw is posted, and results go live in real-time. Focus on your run, not paperwork.', icon: Trophy, color: '#34C759' },
              ].map((item, i) => (
                <div key={i} className="relative bg-[#161616] border border-[#262626] rounded-xl p-6 hover:border-[#262626] transition-all" data-testid={`how-step-${i}`}>
                  <div className="absolute -top-3 -left-1 w-8 h-8 rounded-full flex items-center justify-center text-sm font-black text-black" style={{ background: item.color }}>
                    {item.step}
                  </div>
                  <div className="mt-2">
                    <item.icon size={28} weight="duotone" style={{ color: item.color }} className="mb-3" />
                    <h3 className="text-lg font-bold text-white mb-2" style={{fontFamily: 'Outfit'}}>{item.title}</h3>
                    <p className="text-[#9E9E9E] text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Highlights — Producers */}
        <section className="py-16 sm:py-20 bg-[#111]" data-testid="features-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-[10px] sm:text-xs tracking-[0.2em] uppercase font-semibold text-[#0096FF]">FOR PRODUCERS</span>
              <h2 className="text-2xl sm:text-3xl font-black text-white mt-2" style={{fontFamily: 'Outfit'}}>Everything You Need to Run a Show</h2>
              <p className="text-[#9E9E9E] text-sm mt-2 max-w-xl mx-auto">Replace your spreadsheets, paper entries, and manual payouts with one powerful platform.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { icon: Horse, title: 'Online Entries', desc: 'Riders enter classes, sidepots, and rollovers online. Bulk import from CSV, Excel, PDF, or even photos of handwritten lists.', color: '#0096FF' },
                { icon: Timer, title: 'Live Scoring & Results', desc: 'Timekeeper enters times, results update instantly on the leaderboard. Livestream embedded right alongside scores.', color: '#FF3B30' },
                { icon: CurrencyDollar, title: 'Automatic Payouts', desc: 'Up to 15-place payout splits with tie handling, division breakdowns, and association fee deductions. Calculate in one click.', color: '#34C759' },
                { icon: MapTrifold, title: 'Interactive Stall Maps', desc: 'Upload your facility map. Riders zoom in to find their barn and select the exact stall they want — just like picking a seat.', color: '#FF9500' },
                { icon: Users, title: 'Kiosk & Office Roles', desc: 'Set up gate entry kiosks, timekeeper logins, and office staff — each with PIN access and the right permissions.', color: '#AF52DE' },
                { icon: ChartLineUp, title: 'Multi-Day & Divisions', desc: 'Day tabs, 2D/3D/4D divisions, averages, series tracking, and show templates you can reuse every week.', color: '#00D4FF' },
              ].map((f, i) => (
                <div key={i} className="bg-[#161616] border border-[#1A1A1A] rounded-xl p-5 hover:-translate-y-1 hover:border-[#262626] transition-all duration-300" data-testid={`feature-card-${i}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${f.color}15` }}>
                    <f.icon size={22} weight="duotone" style={{ color: f.color }} />
                  </div>
                  <h3 className="text-base font-bold text-white mb-1.5" style={{fontFamily: 'Outfit'}}>{f.title}</h3>
                  <p className="text-[#9E9E9E] text-sm leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Find a Show */}
        <section className="py-16 sm:py-20 bg-[#0A0A0A]" data-testid="find-show-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <span className="text-[10px] sm:text-xs tracking-[0.2em] uppercase font-semibold text-[#FF9500]">UPCOMING SHOWS</span>
              <h2 className="text-2xl sm:text-3xl font-black text-white mt-2" style={{fontFamily: 'Outfit'}}>Find a Show Near You</h2>
            </div>

            {/* Search */}
            <div className="max-w-md mx-auto mb-8">
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or location..."
                  className="w-full pl-10 pr-4 py-3 bg-[#161616] border border-[#262626] rounded-lg text-white text-sm focus:outline-none focus:border-[#0096FF] transition-colors"
                  data-testid="show-search-input"
                />
              </div>
            </div>

            {filteredShows.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredShows.map((show) => (
                  <button key={show.id} onClick={() => navigate(`/shows/${show.id}`)}
                    className="text-left bg-[#161616] border border-[#1A1A1A] rounded-xl p-5 hover:border-[#0096FF]/40 hover:-translate-y-0.5 transition-all duration-200"
                    data-testid={`show-card-${show.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-base font-bold text-white truncate" style={{fontFamily: 'Outfit'}}>{show.name}</h3>
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#FF3B30]/15 text-[#FF3B30] flex-shrink-0 ml-2">LIVE</span>
                    </div>
                    <div className="flex items-center gap-3 text-[#9E9E9E] text-xs">
                      <span className="flex items-center gap-1"><CalendarBlank size={12} />{new Date(show.start_date).toLocaleDateString()}</span>
                      {show.location && <span className="flex items-center gap-1 truncate"><MapPin size={12} />{show.location}</span>}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[10px] text-[#555]">{(show.classes || []).length} classes</span>
                      <span className="text-[#0096FF] text-xs font-semibold flex items-center gap-1">Enter <ArrowRight size={12} /></span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-[#555] text-sm">{searchQuery ? 'No shows match your search' : 'No live shows right now'}</p>
              </div>
            )}
            <div className="text-center mt-6">
              <button onClick={() => navigate('/shows')} className="text-[#0096FF] text-sm font-semibold hover:text-[#00D4FF] transition-colors" data-testid="view-all-shows-btn">
                View All Shows <ArrowRight size={14} className="inline ml-1" />
              </button>
            </div>
          </div>
        </section>

        {/* Works on Any Device */}
        <section className="py-16 sm:py-20 bg-[#111]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center gap-10">
              <div className="flex-1">
                <span className="text-[10px] sm:text-xs tracking-[0.2em] uppercase font-semibold text-[#AF52DE]">MOBILE READY</span>
                <h2 className="text-2xl sm:text-3xl font-black text-white mt-2 mb-4" style={{fontFamily: 'Outfit'}}>Works on Any Device</h2>
                <p className="text-[#9E9E9E] text-sm leading-relaxed mb-4">
                  Install Show Master on your phone like an app — no app store needed. Riders can enter from the parking lot. Timekeepers score from their phone. Office staff manage payments on a tablet. Works even with spotty arena Wi-Fi.
                </p>
                <div className="space-y-2">
                  {['Add to home screen for instant access', 'Works offline for scoring at the arena', 'Real-time results update for spectators'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-[#34C759]/15 flex items-center justify-center flex-shrink-0">
                        <span className="text-[#34C759] text-[10px] font-bold">+</span>
                      </div>
                      <span className="text-[#9E9E9E] text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-1 flex justify-center">
                <div className="relative">
                  <Devices size={200} weight="duotone" className="text-[#262626]" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-[#0096FF] text-3xl font-black" style={{fontFamily: 'Outfit'}}>PWA</p>
                      <p className="text-[#555] text-[10px]">Progressive Web App</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Us */}
        <section className="py-16 sm:py-20 bg-[#0A0A0A]" data-testid="contact-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <span className="text-[10px] sm:text-xs tracking-[0.2em] uppercase font-semibold text-[#FF9500]">GET IN TOUCH</span>
                <h2 className="text-2xl sm:text-3xl font-black text-white mt-2 mb-4" style={{fontFamily: 'Outfit'}}>Need Us to Run Your Show?</h2>
                <p className="text-[#9E9E9E] text-sm leading-relaxed mb-6">
                  Don't want to manage the tech yourself? We'll come to your show and handle everything — entries, scoring, payouts, and live results. Just let us know the details and we'll take care of the rest.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#0096FF]/10 flex items-center justify-center flex-shrink-0">
                      <Envelope size={20} className="text-[#0096FF]" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">Email Us</p>
                      <a href="mailto:thehorseshowmasteradmin@gmail.com" className="text-[#0096FF] text-sm hover:text-[#00D4FF] transition-colors" data-testid="contact-email-link">
                        thehorseshowmasteradmin@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#FF9500]/10 flex items-center justify-center flex-shrink-0">
                      <Phone size={20} className="text-[#FF9500]" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">Call or Text</p>
                      <a href="tel:+15026480949" className="text-[#FF9500] text-sm hover:text-[#FF9500]/80 transition-colors" data-testid="contact-phone-link">
                        1 (502) 648-0949
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#34C759]/10 flex items-center justify-center flex-shrink-0">
                      <ChatCircleDots size={20} className="text-[#34C759]" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">Quick Response</p>
                      <p className="text-[#9E9E9E] text-sm">We typically respond within 24 hours</p>
                    </div>
                  </div>
                </div>
              </div>

              <form onSubmit={handleContactSubmit} className="bg-[#161616] border border-[#1A1A1A] rounded-xl p-6" data-testid="contact-form">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#9E9E9E] mb-1">Your Name</label>
                    <input
                      type="text"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      placeholder="John Smith"
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:outline-none focus:border-[#0096FF] transition-colors"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#9E9E9E] mb-1">Email Address</label>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:outline-none focus:border-[#0096FF] transition-colors"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#9E9E9E] mb-1">Tell Us About Your Show</label>
                    <textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="Show name, dates, location, number of classes, any special needs..."
                      rows={4}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:outline-none focus:border-[#0096FF] transition-colors resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={contactSending}
                    className="w-full py-3 bg-[#FF9500] hover:bg-[#FF9500]/80 text-black font-semibold rounded-lg transition-colors disabled:opacity-50 text-sm"
                    data-testid="contact-submit-btn"
                  >
                    {contactSending ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-24 bg-[#0A0A0A]" data-testid="cta-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-4xl font-black text-white mb-4" style={{fontFamily: 'Outfit'}}>Ditch the Manual Spreadsheets.</h2>
            <p className="text-[#9E9E9E] text-sm sm:text-base mb-8 max-w-lg mx-auto">
              Join producers who run faster, cleaner shows. Set up your first show in minutes — and when you do need a spreadsheet, we'll export one for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => navigate('/register')}
                className="px-8 py-4 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-all duration-300 hover:-translate-y-1 text-lg"
                data-testid="cta-get-started-btn">
                Get Started Free
              </button>
              <button onClick={() => navigate('/shows')}
                className="px-8 py-4 bg-transparent border border-[#262626] hover:border-[#0096FF] text-white rounded-md transition-colors text-lg"
                data-testid="cta-browse-btn">
                Browse Shows
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#111] border-t border-[#1A1A1A] py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_2decf1d9-6688-4b47-98c1-1893beae897a/artifacts/lr28yf8y_43425DE4-4AE4-4784-89F6-4AA746534747.png"
                  alt="Show Master"
                  className="h-8 w-8"
                />
                <span className="text-sm font-bold text-white" style={{fontFamily: 'Outfit'}}>SHOW MASTER</span>
              </div>
              <p className="text-[#555] text-xs">The complete horse show management platform</p>
              <div className="flex items-center gap-4 text-xs">
                <a
                  href="/terms-of-service"
                  className="text-[#9E9E9E] hover:text-white transition-colors"
                  data-testid="footer-terms-link"
                >
                  Terms of Service
                </a>
                <span className="text-[#333]">·</span>
                <a
                  href="/privacy-policy"
                  className="text-[#9E9E9E] hover:text-white transition-colors"
                  data-testid="footer-privacy-link"
                >
                  Privacy Policy
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
